import { useState, useMemo } from 'react';
import {
  Heading,
  Text,
  Badge,
  Button,
  ColorSwatch,
  TokenSlider,
} from '@bq/web-components';
import styles from './TokenTunerSection.module.css';

/** Convert hex (#rrggbb) to "r, g, b" string for rgba(var()) */
function hexToRgb(hex: string): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

interface TokenState {
  primaryColor: string;
  secondaryColor: string;
  fontSize: number;
  spacing: number;
  borderRadius: number;
  shadowIntensity: number;
}

const DEFAULTS: TokenState = {
  primaryColor: '#1975f0',
  secondaryColor: '#7c3aed',
  fontSize: 100,
  spacing: 100,
  borderRadius: 12,
  shadowIntensity: 8,
};

export function TokenTunerSection() {
  const [tokens, setTokens] = useState<TokenState>({ ...DEFAULTS });

  const update = <K extends keyof TokenState>(key: K, value: TokenState[K]) =>
    setTokens((prev) => ({ ...prev, [key]: value }));

  const reset = () => setTokens({ ...DEFAULTS });

  const isDefault = useMemo(
    () => JSON.stringify(tokens) === JSON.stringify(DEFAULTS),
    [tokens],
  );

  // Build inline CSS custom properties to override tokens in the preview scope
  const previewStyle = useMemo(() => {
    const scale = tokens.fontSize / 100;
    const spaceScale = tokens.spacing / 100;
    return {
      '--bq-p500': hexToRgb(tokens.primaryColor),
      '--bq-p700': hexToRgb(tokens.secondaryColor),
      '--bq-r1': `${1.125 * scale}rem`,
      '--bq-r2': `${1 * scale}rem`,
      '--bq-r3': `${0.875 * scale}rem`,
      '--bq-heading-3': `${1.5 * scale}rem`,
      '--bq-size1': `${1 * spaceScale}rem`,
      '--bq-size1-5': `${1.5 * spaceScale}rem`,
      '--bq-size2': `${2 * spaceScale}rem`,
      '--bq-br3': `${tokens.borderRadius}px`,
      '--bq-shadow-2': `0 4px ${tokens.shadowIntensity * 2}px rgba(0, 0, 0, ${tokens.shadowIntensity / 100})`,
    } as React.CSSProperties;
  }, [tokens]);

  return (
    <section
      id="tuner"
      data-crd-id="r4.bq-token-tuner-section"
      data-crd-layer="r4"
      className={styles.section}
    >
      <div className={styles.intro}>
        <Badge variant="default">Feature #5</Badge>
        <Heading level={2} as={2} align="center">
          Token Tuner
        </Heading>
        <Text size="r1" align="center" color="n500">
          Drag the sliders to modify R1 design tokens and watch the preview
          components update in real time. Every component in CRD inherits from
          the same token layer.
        </Text>
      </div>

      <div className={styles.workspace}>
        {/* Controls panel */}
        <div className={styles.controls}>
          <div className={styles.colorRow}>
            <ColorSwatch
              label="Primary Color"
              value={tokens.primaryColor}
              onChange={(v) => update('primaryColor', v)}
            />
            <ColorSwatch
              label="Secondary Color"
              value={tokens.secondaryColor}
              onChange={(v) => update('secondaryColor', v)}
            />
          </div>

          <TokenSlider
            category="Typography"
            items={[
              {
                token: 'fontSize',
                label: 'Font Size Scale',
                value: tokens.fontSize,
                min: 60,
                max: 150,
                unit: '%',
                onChange: (v) => update('fontSize', v),
              },
            ]}
          />

          <TokenSlider
            category="Spacing"
            items={[
              {
                token: 'spacing',
                label: 'Spacing Scale',
                value: tokens.spacing,
                min: 50,
                max: 200,
                unit: '%',
                onChange: (v) => update('spacing', v),
              },
            ]}
          />

          <TokenSlider
            category="Shape"
            items={[
              {
                token: 'borderRadius',
                label: 'Border Radius',
                value: tokens.borderRadius,
                min: 0,
                max: 24,
                unit: 'px',
                onChange: (v) => update('borderRadius', v),
              },
              {
                token: 'shadowIntensity',
                label: 'Shadow Intensity',
                value: tokens.shadowIntensity,
                min: 0,
                max: 30,
                unit: 'px',
                onChange: (v) => update('shadowIntensity', v),
              },
            ]}
          />

          <Button
            variant="secondary"
            size="small"
            onClick={reset}
            disabled={isDefault}
          >
            Reset Defaults
          </Button>
        </div>

        {/* Live preview */}
        <div className={styles.preview} style={previewStyle}>
          <div className={styles.previewCard}>
            <Heading level={3} as={3}>
              Sample Heading
            </Heading>
            <Text size="r1">
              This text responds to font size and spacing changes in real time.
              Every token cascades through the CRD hierarchy.
            </Text>
            <Text size="r2" color="n500">
              Smaller body text also inherits from the same R1 scale.
            </Text>
            <div className={styles.previewActions}>
              <Button variant="primary" size="large">
                Primary
              </Button>
              <Button variant="secondary" size="small">
                Secondary
              </Button>
              <Badge variant="success">Live</Badge>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
